/**
 * First-party event tracking. Buffers events in memory, flushes every
 * FLUSH_INTERVAL_MS (or sooner if the buffer fills), and uses navigator.sendBeacon
 * on page unload so trailing events aren't lost.
 *
 * NO PII: never pass firstName, email, phone, free-text, etc. — server also
 * strips a denylist as defense-in-depth.
 */

import i18n from "../i18n";
import { abTestingService } from "./abTestingService";

// Prefix with PUBLIC_URL because the app is served from a sub-path in prod
// (e.g. https://topaesthetics.com/app/quote/). A bare "/api/events" hits the
// origin root and bypasses the EC2 reverse proxy that forwards /app/quote/*
// to Node, which would 404 silently (fetch doesn't throw on 4xx).
const ENDPOINT = `${process.env.PUBLIC_URL || ""}/api/events`;
const FLUSH_INTERVAL_MS = 5000;
const FLUSH_BATCH_SIZE = 10;
const MAX_BUFFER = 100;
const MAX_PER_FLUSH = 50; // mirrors server MAX_BATCH
const SESSION_KEY = "xthetix_session_id";

type EventPayload = Record<string, unknown> | undefined;

type QueuedEvent = {
  eventName: string;
  sessionId: string;
  path: string;
  payload?: EventPayload;
  device: ReturnType<typeof getDeviceInfo>;
  referrer?: string;
  quoteId?: string;
  userId?: string;
  language?: string;
  timestamp: string;
};

let buffer: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let cachedSessionId: string | null = null;
let contextQuoteId: string | undefined;
let contextUserId: string | undefined;
// Active A/B test assignments for this session, auto-attached to every event's
// payload as `abTests` so the server can scope exposure + funnel-step curves to
// a specific test (events otherwise carry only a bare `variant`, untied to a
// testId). Set by the quote funnel via setEventContext. Going-forward only.
let contextAbTests: Array<{ testId: string; variant: string }> | undefined;
// Flipped permanently once the funnel opts in via setEventContext({abTests}).
// From then on assignments are read LIVE from storage at fire time instead of
// from the snapshot: variants can be assigned mid-session during render (e.g.
// the procedure-focus enroller on the first step), after the provider's
// snapshot effect already ran — a snapshot would leave every event between
// enrollment and the first draft untagged for that test.
let abTestsContextEnabled = false;

function getSessionId(): string {
  if (cachedSessionId) return cachedSessionId;
  try {
    const existing = window.localStorage.getItem(SESSION_KEY);
    if (existing) {
      cachedSessionId = existing;
      return existing;
    }
  } catch {
    /* localStorage may be disabled (private mode, partitioned iframe) */
  }
  const fresh =
    (typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2)) +
    "-" +
    Date.now().toString(36);
  try {
    window.localStorage.setItem(SESSION_KEY, fresh);
  } catch {
    /* ignore */
  }
  cachedSessionId = fresh;
  return fresh;
}

function getDeviceInfo() {
  if (typeof window === "undefined") return undefined;
  return {
    userAgent: navigator.userAgent,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
    timezone:
      typeof Intl !== "undefined"
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : undefined,
  };
}

/**
 * Attach a quoteId to all subsequent events in this session. Pass undefined
 * to clear (e.g. on logout / quote reset).
 */
export function setEventContext(ctx: {
  quoteId?: string;
  userId?: string;
  abTests?: Array<{ testId: string; variant: string }>;
}): void {
  if ("quoteId" in ctx) contextQuoteId = ctx.quoteId;
  if ("userId" in ctx) contextUserId = ctx.userId;
  if ("abTests" in ctx) {
    contextAbTests = ctx.abTests;
    abTestsContextEnabled = true;
  }
}

// Cap combined client_error + graphql_error emission per page load — a
// throwing loop or a repeated GraphQL failure can otherwise flood the buffer
// in seconds (2026-08-18 prod incident: one tab emitted 2,708 client_error
// "Script error." events in 107s, 85% of that fortnight's total error
// volume). The server's per-IP token bucket (events-routes.ts) is a coarser,
// separate backstop and doesn't stop a single tab's burst; this mirrors the
// PHP landing site's per-load site_error cap (ta-events.js).
const ERROR_EVENT_CAP = 20;
const CAPPED_ERROR_EVENTS = new Set(["client_error", "graphql_error"]);
let errorEventCount = 0;

export function trackEvent(eventName: string, payload?: EventPayload): void {
  if (typeof window === "undefined") return;
  let effectivePayload = payload;
  if (CAPPED_ERROR_EVENTS.has(eventName)) {
    if (errorEventCount >= ERROR_EVENT_CAP) return;
    errorEventCount++;
    // Flag the final allowed event so reviews can tell capping occurred
    // instead of reading a suspiciously round drop-off as a fixed bug.
    if (errorEventCount === ERROR_EVENT_CAP) {
      effectivePayload = { ...(payload || {}), emit_cap_reached: true };
    }
  }
  if (buffer.length >= MAX_BUFFER) {
    // Drop oldest rather than blocking. Better than runaway memory.
    buffer.splice(0, buffer.length - MAX_BUFFER + 1);
  }
  // Auto-attach active A/B assignments so the server can scope exposure +
  // funnel-step curves per test. Only for sessions that opted in via
  // setEventContext (the funnel), to avoid bloating unrelated events. Read
  // live at fire time so mid-session assignments are included; fall back to
  // the snapshot if the storage read throws.
  let abTests = contextAbTests;
  if (abTestsContextEnabled) {
    try {
      abTests = abTestingService.getActiveVariants();
    } catch {
      /* keep the snapshot */
    }
  }
  const finalPayload =
    abTests && abTests.length > 0
      ? { ...(effectivePayload || {}), abTests }
      : effectivePayload;
  buffer.push({
    eventName,
    sessionId: getSessionId(),
    path: window.location.pathname,
    payload: finalPayload,
    device: getDeviceInfo(),
    referrer: document.referrer || undefined,
    quoteId: contextQuoteId,
    userId: contextUserId,
    language: i18n.language || undefined,
    timestamp: new Date().toISOString(),
  });
  if (buffer.length >= FLUSH_BATCH_SIZE) {
    flush();
  } else {
    scheduleFlush();
  }
}

function scheduleFlush(): void {
  if (flushTimer != null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
}

export function flush(useBeacon = false): void {
  if (buffer.length === 0) return;
  if (flushTimer != null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  const batch = buffer.splice(0, MAX_PER_FLUSH);
  const body = JSON.stringify({ events: batch });

  if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
    try {
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon(ENDPOINT, blob);
      if (ok) return;
    } catch {
      /* fall through to fetch */
    }
  }

  // keepalive=true allows the request to outlive the page on most browsers.
  fetch(ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
    credentials: "same-origin",
  }).catch(() => {
    /* never let analytics break the app */
  });
}

// Errors that fire from window.onerror but are NOT app bugs — they swamp the
// usability dashboard and bury real signal. Dropped at the capture site so they
// never reach tracking. Matched against the error message.
const CLIENT_ERROR_DENYLIST: RegExp[] = [
  // Injected by a browser-extension content-script messaging bridge, e.g.
  // "Object Not Found Matching Id:3, MethodName:update, ParamCount:4". Thrown
  // from inside our bundle but originating in extension code — unfixable from
  // app code, and ~1,240 events/week from a handful of coordinator tabs.
  /Object Not Found Matching Id:/i,
  // Benign layout-reflow notice some browsers raise as an error event; never
  // affects the user (https://stackoverflow.com/q/49384120).
  /ResizeObserver loop/i,
];

// Suppressed only when the stack proves foreign provenance — Safari masks
// injected/extension frames as webkit-masked-url://, which our own bundle's
// frames never are. A first-party error that happens to minify to the same
// message keeps recording (no stack, or a stack with real frames ⇒ not
// suppressed).
const FOREIGN_STACK_DENYLIST: Array<{ message: RegExp; stack: RegExp }> = [
  // iOS Safari extension noise: "undefined is not an object (evaluating
  // 'o.id')" — provenance confirmed 2026-08-03 via errorName + masked stacks.
  { message: /evaluating 'o\.id'/, stack: /webkit-masked-url:/ },
];

export function captureClientError(
  error: unknown,
  context?: Record<string, unknown>,
): void {
  // A foreign (non-Error) rejection reason — e.g. a cross-realm object thrown
  // by a browser-extension content script — gets a synthesized Error here so
  // the rest of the app can rely on .message/.stack. But synthesizing at the
  // reporter destroys the real throw-site stack, and String(error) folds the
  // class into the message. Prefer the reason's own stack/name when it has
  // one; only fall back to the synthesized Error when it doesn't.
  const raw = error as { stack?: unknown; name?: unknown; message?: unknown } | null;
  const err = error instanceof Error ? error : new Error(String(error));
  const stack = typeof raw?.stack === "string" ? raw.stack : err.stack;
  const name = typeof raw?.name === "string" ? raw.name : err.name;
  // Same preference for the foreign reason's own message: String(error) on an
  // error-like object with a broken toString yields "[object Object]".
  const message =
    (typeof raw?.message === "string" && raw.message) || err.message || "";
  if (CLIENT_ERROR_DENYLIST.some((re) => re.test(message))) return;
  if (
    FOREIGN_STACK_DENYLIST.some(
      (e) => e.message.test(message) && e.stack.test(stack ?? ""),
    )
  )
    return;
  trackEvent("client_error", {
    message: message.slice(0, 500),
    stack: stack?.slice(0, 4000),
    // Emitted as `errorName`, not `name` — the server's PII denylist strips
    // any payload key named "name" (defense-in-depth against firstName/
    // lastName), which silently swallowed the error class on every row.
    errorName: name,
    ...context,
  });
}

let bootstrapPingFired = false;

/**
 * Fire as early as possible from index.tsx so we can detect users whose JS
 * bundle executed but who never made it deep enough to fire the in-app
 * tracking events (e.g. Samsung Internet users bailing during render).
 * Without this, our analytics has a survivorship bias — sessions that crash
 * before QuoteApp mounts are invisible.
 */
export function emitBootstrapPing(): void {
  if (bootstrapPingFired) return;
  if (typeof window === "undefined") return;
  bootstrapPingFired = true;
  trackEvent("client_bootstrap", {
    landingPath: window.location.pathname,
    // The OBS overlay's query string is its secret key; never store it.
    landingSearch: /\/live\/overlay(?:\/|$)/.test(window.location.pathname)
      ? ""
      : window.location.search?.slice(0, 256),
    visualViewportHeight: window.visualViewport?.height ?? null,
    documentReadyState: document.readyState,
    hasLocalStorage: (() => {
      try {
        window.localStorage.setItem("__xt_probe", "1");
        window.localStorage.removeItem("__xt_probe");
        return true;
      } catch {
        return false;
      }
    })(),
    hasRandomUUID: typeof crypto !== "undefined" && !!crypto.randomUUID,
    hasSendBeacon:
      typeof navigator !== "undefined" && !!navigator.sendBeacon,
  });
}

/**
 * If `err`/`message` looks like a hashed-chunk load failure (a stale tab after a
 * deploy that deleted old chunks), reload to jump to the new shell.
 *
 * Thrash guard: a genuinely-broken deploy (a chunk that is truly absent, not
 * just stale) can't be fixed by reloading, so a pure time window would loop
 * forever. We cap reloads per tab session, debounce bursts (several chunks
 * failing on one load), and reset the counter when the previous reload was long
 * ago (a distinct, later deploy incident).
 *
 * Exported so the React ErrorBoundary can route lazy-route ChunkLoadErrors here
 * too — those are thrown during render and caught by the boundary, so they never
 * reach window.onerror.
 */
export function maybeReloadOnChunkError(
  err: unknown,
  message: string,
  filename?: string
): void {
  const name = (err as any)?.name;
  const isAssetScript =
    !!filename && /\/static\/.*\.(?:js|css)(?:$|\?)/i.test(filename);
  const isChunkError =
    name === "ChunkLoadError" ||
    /Loading (?:CSS )?chunk [\w-]+ failed/i.test(message) ||
    (isAssetScript && /Unexpected token '<'|SyntaxError/i.test(message));
  if (!isChunkError) return;
  try {
    const COUNT_KEY = "__chunkReloadCount";
    const TS_KEY = "__chunkReloadAt";
    const now = Date.now();
    const last = Number(sessionStorage.getItem(TS_KEY) || 0);
    // Debounce: several chunks can fail on a single load — reload once per burst.
    if (now - last < 10000) return;
    // A reload long ago is a separate incident (a later deploy) — reset the count.
    const count =
      now - last > 5 * 60 * 1000
        ? 0
        : Number(sessionStorage.getItem(COUNT_KEY) || 0);
    // Hard cap: if the NEW shell is itself broken, reloading can't fix it.
    // Give up after 2 attempts and let the error surface instead of thrashing.
    if (count >= 2) return;
    sessionStorage.setItem(COUNT_KEY, String(count + 1));
    sessionStorage.setItem(TS_KEY, String(now));
  } catch {
    /* sessionStorage blocked (private mode/iframe) — fall through, reload once */
  }
  try {
    flush(true);
  } catch {
    /* ignore */
  }
  window.location.reload();
}

let globalHandlersInstalled = false;

export function installGlobalErrorHandlers(): void {
  if (globalHandlersInstalled) return;
  if (typeof window === "undefined") return;
  globalHandlersInstalled = true;

  window.addEventListener("error", (e) => {
    // Cross-origin script errors are sanitized to message="Script error.",
    // filename="", lineno=0, colno=0, error=null. We can't recover the
    // original message, but capturing what scripts ARE on the page helps
    // identify the injecting origin (e.g. Meta in-app browser's iabjs://...).
    const sanitized =
      !e.error && e.message === "Script error." && !e.filename && !e.lineno;
    const ctx: Record<string, unknown> = {
      filename: e.filename,
      lineno: e.lineno,
      colno: e.colno,
      sanitized,
      timeSincePageLoadMs: Math.round(performance.now()),
    };
    if (sanitized) {
      try {
        const origins = new Set<string>();
        const sample: string[] = [];
        for (const s of Array.from(document.scripts)) {
          if (!s.src) continue;
          try {
            origins.add(new URL(s.src).origin);
          } catch {
            origins.add(s.src.slice(0, 60));
          }
          if (sample.length < 10) sample.push(s.src.slice(0, 200));
        }
        ctx.scriptOrigins = Array.from(origins).slice(0, 10);
        ctx.scriptSampleCount = document.scripts.length;
      } catch {
        /* ignore */
      }
    }
    captureClientError(e.error || new Error(e.message), ctx);
    maybeReloadOnChunkError(e.error, e.message, e.filename);
  });

  window.addEventListener("unhandledrejection", (e) => {
    captureClientError(e.reason ?? new Error("unhandledrejection"), {
      timeSincePageLoadMs: Math.round(performance.now()),
    });
    maybeReloadOnChunkError(
      e.reason,
      String((e.reason && (e.reason as any).message) ?? e.reason ?? "")
    );
  });

  window.addEventListener("pagehide", () => flush(true));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush(true);
  });
}
